import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { List } from "semantic-ui-react";
import { isNonEmptyArray, isNonEmptyMap, isNonEmptyString, isFunction, isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";
import { addAdminTeamMemberWithRole, removeAdminTeamMember, notifyError } from "utils";
import { IS_DEV } from "globals/constants";
import useDictionary from "hooks/useDictionary";
import useGroupRoles from "hooks/useGroupRoles";
import usePersonSearch from "components/flowable/FlowablePersonSearch/components/hooks/usePersonSearch";
import Button from "@cayuse/shared-react.components.button";
import CovertButton from "@cayuse/shared-react.components.covert-button";
import Dropdown from "components/Dropdown";
import Icon from "@cayuse/shared-react.components.icon";
import IconButton from "@cayuse/shared-react.components.icon-button";
import Popup from "@cayuse/shared-react.components.popup";
import SearchDropdown from "@cayuse/shared-react.components.search-dropdown";


import styles from "./styles.css";

const AssignSelected = ({
	editMembers,
	instanceFeedItemSelections,
	isOpen,
	onOpen,
	onClose,
	onTeamModification,
	triggerType,
	triggerLabel,
	adminRole,
	formType,
	isFeed,
	adminTeamMembers: incomingAdminTeamMembers
}) => {
	const [ selectedRole, setSelectedRole ] = useState(null);
	const [ selectedUser, setSelectedUser ] = useState({});
	const [ userOptions, setUserOptions ] = useState([]);
	const [ currentFocusedElement, setCurrentFocusedElement ] = useState(null);
	const { search } = usePersonSearch();
	const isReadyToAssign = isNonEmptyString(selectedRole) && isNonEmptyObject(selectedUser);
	const { roles } = useGroupRoles();
	const spAdminRole = roles.find(role => role.name === adminRole);

	const formattedRoles = spAdminRole
		? [ {
			key: spAdminRole.id,
			value: spAdminRole.id,
			text: spAdminRole.name,
			role: spAdminRole
		} ]
		: [];

	const dictionary = useDictionary("common.assignSelected");
	const hasSelections = isNonEmptyMap(instanceFeedItemSelections);
	const ariaControlsAttr = isOpen ? { [`aria-controls`]: "assignSelectedPopup" } : {};

	const handleOpen = () => {
		if (isFunction(onOpen)) {
			onOpen();
		}
	};

	const handleClose = useCallback(() => {
		setSelectedRole(null);
		setSelectedUser({});
		setUserOptions([]);

		if (isFunction(onClose)) {
			onClose();
		}
	}, [ onClose ]);

	const handleRoleChange = (event, { value: roleId }) => {
		setSelectedRole(roleId);
	};

	const handleUserChange = (event , data) => {
		setSelectedUser(data[0]);
	};

	const options ={
		keyParamName: "personId",
		searchParameters: []
	};

	const getDataPromise = useCallback(searchText => 
		search(searchText, { searchParameters: [{ key: "roles.groupId", val: selectedRole }] }),
	[ selectedUser, selectedRole ]);

	const handleAssignment = useCallback(() => {
		const selectedUserObject = selectedUser?.person;
		const selectedRoleObject = Object.assign({}, formattedRoles.find(({ key }) => selectedRole === key)?.role);


		if (isFeed) {
			const instanceFeedItems = Array.from(instanceFeedItemSelections.values());
			handleClose();

			// TODO: Do we want a "working" indicator somewhere?
			return addAdminTeamMemberWithRole(instanceFeedItems, selectedUserObject, selectedRoleObject, formType)
				.then(idAdminTeamMap => {
					handleClose();
					if (isFunction(onTeamModification)) {
						onTeamModification(idAdminTeamMap, selectedUserObject);
					}

					return idAdminTeamMap;
				})
				.catch(error => {
					const message = IS_DEV ? error.message : dictionary.errors.addTeamMemberFails;
					notifyError(message, error);
				});
		} else {
			if (isFunction(onTeamModification)) {
				const person = {
					fullName: selectedUserObject.fullName,
					personId: selectedUserObject.personId,
					roleId: selectedRoleObject.id,
					roleName: selectedRoleObject.name,
				};

				onTeamModification({ type: "add", person, role: selectedRoleObject });
				handleClose();
			}
		}
	}, [
		userOptions,
		formattedRoles,
		isFeed,
		selectedUser,
		selectedRole,
		instanceFeedItemSelections,
		handleClose,
		onTeamModification,
		dictionary.errors.addTeamMemberFails
	]);

	const handleUnassign = (useCallback)(adminTeamMember => {
		if (isFeed) {
			const instanceFeedItem = instanceFeedItemSelections.values().next().value;

			return removeAdminTeamMember(adminTeamMember, instanceFeedItem, instanceFeedItem.adminTeamMemberData)
				.then(idAdminTeamMap => {
					if (isFunction(onTeamModification)) {
						onTeamModification(idAdminTeamMap);
					}

					return idAdminTeamMap;
				})
				.catch(error => {
					const message = IS_DEV ? error.message : dictionary.errors.removeTeamMemberFails;
					notifyError(message, error);
				})
				.finally(() => {
					handleClose();
				});
		} else {
			if (isFunction(onTeamModification)) {
				onTeamModification({ type: "remove", person: adminTeamMember });
			}
		}
	}, [
		isFeed,
		instanceFeedItemSelections,
		onTeamModification,
		dictionary.errors.removeTeamMemberFails,
		handleClose
	]);

	const assignmentSelectedButton = useMemo(() => {
		const label = isNonEmptyString(triggerLabel)
			? triggerLabel
			: dictionary.assignSelected || "Assign selected";

		return triggerType === "link"
			? <CovertButton aria-expanded={isOpen} {...ariaControlsAttr} id="assignSelectedButton">{label}</CovertButton>
			: (
				<IconButton
					aria-expanded={isOpen}
					{...ariaControlsAttr}
					id="assignSelectedButton"
					icon="caret-down"
					labelPosition="right"
					primary
					size="small"
					disabled={!hasSelections}
					tooltip={!hasSelections && "Please select items from table below"}
				>
					{label}
				</IconButton>
			);
	}, [ triggerLabel, dictionary.assignSelected, triggerType, hasSelections, isOpen ]);

	const assignRolesButton = useMemo(() => {
		return (
			<div className="ui actions">
				<Button primary
					disabled={!isReadyToAssign}
					onClick={handleAssignment}
					tooltip={!isReadyToAssign && "Please select a role and a person"}
				>
					{dictionary.assignRoles || "Assign Roles"}
				</Button>
			</div>
		);
	}, [ dictionary, isReadyToAssign, handleAssignment ]);

	

	const renderPopupMemberList = useCallback(member => {
		const roleName = Array.isArray(member.roles)
			? member.roles[0]?.roleName
			: member.roleName;

		return (
			<List.Item key={member.personId} className={styles.memberListItem}>
				{`${member.fullName} - ${roleName}`}
				<CovertButton onClick={() => handleUnassign(member)} aria-label={`Remove member: ${member.fullName} - ${roleName}`}>
					<Icon name="times" className={styles.removeMemberButton}/>
				</CovertButton>
			</List.Item>
		);
	}, [ handleUnassign ]);

	const adminTeamMemberList = useMemo(() => {
		if (isFeed) {
			return "";
		}

		return isNonEmptyArray(incomingAdminTeamMembers)
			? incomingAdminTeamMembers.map(renderPopupMemberList)
			: "";
	}, [ incomingAdminTeamMembers, isFeed, renderPopupMemberList ]);

	const feedAdminTeamMemberList = useMemo(() => {
		if (!editMembers) {
			return "";
		}
		if (instanceFeedItemSelections.size < 1 || instanceFeedItemSelections.size > 1) {
			console.error("There must be only one selection to edit. size: %s, selections: %o", instanceFeedItemSelections.size, instanceFeedItemSelections);

			return "";
		}

		const soleInstance = instanceFeedItemSelections.values().next().value;
		if (!isNonEmptyArray(soleInstance.adminTeamMemberData)) {
			return "";
		}

		const memberList = soleInstance.adminTeamMemberData.map(renderPopupMemberList);

		return (
			<div className={styles.editMembers}>
				<List bulleted>
					{memberList}
				</List>
			</div>
		);
	}, [ editMembers, handleUnassign, instanceFeedItemSelections ]);

	const popupContent = useMemo(() => (
		<div className={`ui form fields ${styles.fieldWrapper}`}>
			<Button
				onClick={() => {
					handleClose();
					document.querySelector("#assignSelectedButton").focus();
				}}
				className={`${styles.closeAssignSelectedPopupButton} closeAssignSelectedPopupButton`}
				ariaLabel="Close pop-up"
				tertiary
			>
				<Icon name="times" />
			</Button>
			{(feedAdminTeamMemberList || adminTeamMemberList) && (
				<div className={styles.memberList}>
					{ isFeed
						? feedAdminTeamMemberList
						: adminTeamMemberList
					}
				</div>
			)}
			<div className="field">
				<label htmlFor="roleDropdownView">{dictionary.selectRole || "Select Role"}
					<Dropdown
						id="roleDropdownView"
						placeholder={dictionary.selectRolePlaceholder || "Select a Role..."}
						selection
						fluid
						options={formattedRoles}
						onChange={handleRoleChange}
						value={selectedRole}
						aria-label={dictionary.selectRolePlaceholder || "Select a Role..."}
						tabIndex={"0"}
					/>
				</label>
			</div>

			<div className="field">
				<label htmlFor="personAssignmentInputView">{dictionary.selectPerson || "Select Person"}</label>
				<SearchDropdown
					value={selectedUser?.key}
					search={(options) => options}
					disabled={!isNonEmptyString(selectedRole)}
					selection
					selectOnNavigation={false}
					forceSelection={false}
					selectOnBlur={false}
					onGetDataPromise={getDataPromise}
					searchingMessage={"Searching"} // todo: dictionary
					placeholder={dictionary.selectPersonPlaceholder || "Select a Person..."}
					onChange={handleUserChange}
					narrowCriteriaHint={{show: true}}
					className="personFinder"
					fieldLabel={dictionary.selectPerson || "Select a Person"}
				/>
			</div>

			{assignRolesButton}
		</div>
	), [ formattedRoles, selectedRole, userOptions, selectedUser ]);

	// returns true if element is not visible on the screen
	const isHidden = el => {
		const style = window.getComputedStyle(el);

		return ((style.display === "none") || (style.visibility === "hidden"));
	};

	// returns an array of the modal's child DOM nodes that may receive keyboard input in the order that they appear in the DOM.
	const getKeyboardFocusableElements = popupElement => {
		if (popupElement === null) { return; }

		// Focusable HTML elements include any non-disabled a, button, input, textarea, select, details, not hidden elements and elements assigned a tabindex of >= 0
		return [ ...popupElement.querySelectorAll(
			"a,  button, input, textarea, select, details,[tabindex]:not([tabindex=\"-1\"])"
		) ].filter(el => !el.hasAttribute("disabled") && !isHidden(el));
	};

	// trap the keyboard focus inside the modal while it is open
	const handleKeyDown = evt => {
		const popupElement = document.querySelector("#assignSelectedPopup");
		const focusableElements = getKeyboardFocusableElements(popupElement);

		// if we are not tabbing, if the modal is not open, or if we have no tabbable elements,
		// we have nothing to handle, so return.
		if (evt.key !== "Tab" || !isOpen || focusableElements.length === 0) {
			return;
		}
		// stop the tab from doing it's prescribed action(s)
		evt.preventDefault();

		// if the shift key is applied, we are reverse tabbing, and the first and last
		// focusable elements are reversed, as is the currently focused element
		const terminalElementIndex = evt.shiftKey ? 0 : focusableElements.length - 1;

		if (document.activeElement === focusableElements[terminalElementIndex]) {
			const wrapAroundElementIndex = evt.shiftKey ? focusableElements.length - 1 : 0;

			// if we are currently focused on a terminal element, wrap the focus around to the other
			// terminal focusable element
			focusableElements[wrapAroundElementIndex] && focusableElements[wrapAroundElementIndex].focus();
			setCurrentFocusedElement(wrapAroundElementIndex);
		} else {
			const nextCurrentItemIndex = evt.shiftKey ? currentFocusedElement - 1 : currentFocusedElement + 1;

			// else we focus the next current item
			focusableElements[nextCurrentItemIndex].focus();
			setCurrentFocusedElement(nextCurrentItemIndex);
		}
	};

	const getFirstFocusableElementIndex = elements => {
		// we have no focusable elements, so return null
		if (elements.length === 0) {
			return null;
		}

		// don't autofocus the close button unless it's the only focusable element on the page
		return elements[0].classList.contains("closeAssignSelectedPopupButton") && elements.length > 1 ? 1 : 0;
	};

	// focus the first focusable element in the modal if one exists and initialize the focus trap index
	useEffect( () => {
		const popupElement = document.querySelector("#assignSelectedPopup");

		if (popupElement) {
			const focusableElements = getKeyboardFocusableElements(popupElement);
			const firstFocusableElementIndex = getFirstFocusableElementIndex(focusableElements);

			if (firstFocusableElementIndex !== null) {
				setCurrentFocusedElement(firstFocusableElementIndex);
				focusableElements[firstFocusableElementIndex].focus();
			}
		}
	}, [ isOpen ]);

	return (
		<Popup
			flowing={true}
			content={popupContent}
			eventsEnabled={false}
			on="click"
			onClose={handleClose}
			onOpen={handleOpen}
			open={isOpen}
			trigger={assignmentSelectedButton}
			id="assignSelectedPopup"
			onKeyDown={handleKeyDown}
		/>
	);
};

AssignSelected.propTypes = {
	isOpen: PropTypes.bool,
	onOpen: PropTypes.func,
	onClose: PropTypes.func,
	onTeamModification: PropTypes.func,
	instanceFeedItemSelections: PropTypes.object,
	triggerLabel: PropTypes.string,
	triggerType: PropTypes.oneOf([ "button", "link" ])
};

export default AssignSelected;
